import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  AdminTTDDialogComponent,
  AdminTTDDialogMode,
  AdminTTDDialogClose,
} from 'src/app/components/dialogs/admin/admin-ttd/admin-ttd-dialog.component';

import {
  TtdPartner,
  TtdAdvertiser,
} from 'src/app/models/services/admin/ttd';

interface OpenPayload {
  mode: AdminTTDDialogMode;
  partner?: TtdPartner | null;
  advertiser?: TtdAdvertiser | null;
  usedIds?: Set<string> | string[];
  usedAdvIds?: Set<string> | string[];
}

@Injectable({ providedIn: 'root' })
export class AdminTTDDialogService {
  constructor(private modalService: NgbModal) {}

  open(payload: OpenPayload): Promise<AdminTTDDialogClose> {
    const isDelete =
      payload.mode === 'deletePartner' || payload.mode === 'deleteAdvertiser';

    const size =
      payload.mode === 'createPartner' || payload.mode === 'editPartner'
        ? 'lg'
        : isDelete
        ? 'sm'
        : 'md';

    const ref = this.modalService.open(AdminTTDDialogComponent, {
      size,
      windowClass: `ttd-modal ${isDelete ? 'ttd-modal--delete' : 'ttd-modal--form'}`,
    });

    const cmp = ref.componentInstance as AdminTTDDialogComponent;
    cmp.mode = payload.mode;
    cmp.partner = payload.partner ?? null;
    cmp.advertiser = payload.advertiser ?? null;
    cmp.usedIds =
      payload.usedIds instanceof Set
        ? payload.usedIds
        : new Set(payload.usedIds ?? []);
    cmp.usedAdvIds =
      payload.usedAdvIds instanceof Set
        ? payload.usedAdvIds
        : new Set(payload.usedAdvIds ?? []);

    return new Promise<AdminTTDDialogClose>((resolve) => {
      cmp.accept = (res: AdminTTDDialogClose) => {
        resolve(res);
        ref.close('accept');
      };
      cmp.dismiss = () => {
        resolve({ action: 'cancel' });
        ref.dismiss('cancel');
      };
    });
  }

  createPartner(usedIds: Set<string> | string[] = []) {
    return this.open({ mode: 'createPartner', usedIds });
  }

  editPartner(partner: TtdPartner, usedIds: Set<string> | string[] = []) {
    return this.open({ mode: 'editPartner', partner, usedIds });
  }

  confirmDeletePartner(partner: TtdPartner) {
    return this.open({ mode: 'deletePartner', partner });
  }

  createAdvertiser(partner: TtdPartner, usedAdvIds: Set<string> | string[] = []) {
    return this.open({ mode: 'createAdvertiser', partner, usedAdvIds });
  }

  editAdvertiser(
    partner: TtdPartner,
    advertiser: TtdAdvertiser,
    usedAdvIds: Set<string> | string[] = []
  ) {
    return this.open({ mode: 'editAdvertiser', partner, advertiser, usedAdvIds });
  }

  confirmDeleteAdvertiser(partner: TtdPartner, advertiser: TtdAdvertiser) {
    return this.open({ mode: 'deleteAdvertiser', partner, advertiser });
  }

  public show = (
    acceptCallback: (data: any) => void,
    rejectCallback?: () => void,
    partnerParameters?: any
  ): void => {
    const mode: AdminTTDDialogMode = partnerParameters ? 'editPartner' : 'createPartner';
    this.open({ mode, partner: partnerParameters ?? null })
      .then((res) => {
        if (res.action === 'createPartner') acceptCallback(res.data);
        else if (res.action === 'updatePartner') acceptCallback({ id: res.id, ...res.data });
        else rejectCallback?.();
      })
      .catch(() => rejectCallback?.());
  };

  public showDelete = (
    partner: TtdPartner,
    acceptCallback: (id: string) => void,
    rejectCallback?: () => void
  ): void => {
    this.confirmDeletePartner(partner)
      .then((res) => {
        if (res.action === 'deletePartner') acceptCallback(res.id);
        else rejectCallback?.();
      })
      .catch(() => rejectCallback?.());
  };
}
